import React, { useState, useRef, useEffect } from 'react'
import { useConfiguration } from '../contexts/ConfigurationContext'
import { Cloud, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react'

function ProviderSelector() {
  const { state, dispatch } = useConfiguration()
  const [isOpen, setIsOpen] = useState(false)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })
  const dropdownRef = useRef(null)
  const triggerRef = useRef(null)

  const getProviderLogo = (providerId) => {
    // In a real app, you'd use actual logos
    const colors = {
      aws: 'bg-orange-100 text-orange-600',
      gcp: 'bg-blue-100 text-blue-600',
      azure: 'bg-blue-100 text-blue-700',
      otc: 'bg-purple-100 text-purple-600',
      ovhcloud: 'bg-blue-100 text-blue-800',
      ionos: 'bg-green-100 text-green-600'
    }
    return colors[providerId] || 'bg-gray-100 text-gray-600'
  }

  const handleProviderSelect = (provider) => {
    dispatch({ type: 'SET_SELECTED_PROVIDER', payload: provider })
    setIsOpen(false)
  }

  const toggleDropdown = () => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
        width: rect.width
      })
    }
    setIsOpen(!isOpen)
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && 
          triggerRef.current && !triggerRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <>
      <div className="card-enterprise">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-8 h-8 bg-gradient-to-br from-primary-100 to-primary-200 rounded-lg flex items-center justify-center">
            <Cloud className="h-4 w-4 text-primary-600" />
          </div>
          <h3 className="text-lg font-bold text-secondary-900">
            Cloud Provider
          </h3>
        </div>

        {state.providers.length === 0 ? (
          <div className="text-center py-8 text-secondary-500">
            <div className="w-12 h-12 bg-secondary-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Cloud className="h-6 w-6 text-secondary-400" />
            </div>
            <p className="font-medium text-sm">No providers available.</p>
            <p className="text-xs mt-1">Please try again later.</p>
          </div>
        ) : (
          <div className="relative">
            {/* Dropdown Trigger */}
            <button
              ref={triggerRef}
              onClick={toggleDropdown}
              className={`w-full flex items-center justify-between p-4 bg-white border-2 rounded-xl transition-all duration-200 ${
                isOpen
                  ? 'border-primary-400 ring-2 ring-primary-100'
                  : 'border-secondary-200 hover:border-primary-300'
              } ${state.selectedProvider ? 'bg-gradient-to-r from-primary-50 to-white' : 'hover:bg-secondary-50'}`}
            >
              <div className="flex items-center space-x-3">
                {state.selectedProvider ? (
                  <>
                    <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${getProviderLogo(state.selectedProvider.id)}`}>
                      <Cloud className="h-4 w-4" />
                    </div>
                    <div className="text-left">
                      <div className="text-sm font-semibold text-secondary-900">
                        {state.selectedProvider.name}
                      </div>
                      <div className="text-xs text-secondary-600">
                        {state.selectedProvider.shortName} • {state.selectedProvider.locations.length} locations
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="h-8 w-8 rounded-lg bg-secondary-100 flex items-center justify-center">
                      <Cloud className="h-4 w-4 text-secondary-400" />
                    </div>
                    <div className="text-left">
                      <div className="text-sm font-medium text-secondary-600">
                        Select a cloud provider
                      </div>
                      <div className="text-xs text-secondary-500">
                        Choose from {state.providers.length} available providers
                      </div>
                    </div>
                  </>
                )}
              </div>
              {isOpen ? (
                <ChevronUp className="h-4 w-4 text-secondary-600" />
              ) : (
                <ChevronDown className="h-4 w-4 text-secondary-600" />
              )}
            </button>
          </div>
        )}

        {/* Selected Provider Summary */}
        {state.selectedProvider && (
          <div className="mt-4 p-4 bg-gradient-to-br from-success-50 to-success-100 rounded-xl border border-success-200">
            <div className="flex items-center space-x-2 mb-2">
              <CheckCircle className="h-4 w-4 text-success-600" />
              <h4 className="text-sm font-semibold text-success-900">
                Selected: {state.selectedProvider.name}
              </h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-success-700">
              <div className="flex items-center space-x-2">
                <span className="font-medium">Regions:</span>
                <span>{state.selectedProvider.locations.length} available</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="font-medium">Certifications:</span>
                <span>{state.selectedProvider.certifications.slice(0, 2).join(', ')}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Portal-style Dropdown Menu - Rendered at root level */}
      {isOpen && (
        <div 
          ref={dropdownRef}
          className="fixed bg-white border border-secondary-200 rounded-xl shadow-2xl max-h-96 overflow-y-auto"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            zIndex: 999999
          }}
        >
          {state.providers.map((provider, index) => (
            <div
              key={provider.id}
              onClick={() => handleProviderSelect(provider)}
              className={`cursor-pointer p-4 hover:bg-primary-50 transition-colors ${
                index !== state.providers.length - 1 ? 'border-b border-secondary-100' : ''
              } ${
                state.selectedProvider?.id === provider.id ? 'bg-primary-50 border-l-4 border-l-primary-500' : ''
              }`}
            >
              <div className="flex items-center space-x-3 mb-3">
                <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${getProviderLogo(provider.id)}`}>
                  <Cloud className="h-4 w-4" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <h4 className="text-sm font-semibold text-secondary-900">
                      {provider.name}
                    </h4>
                    {state.selectedProvider?.id === provider.id && (
                      <CheckCircle className="h-4 w-4 text-primary-600" />
                    )}
                  </div>
                  <p className="text-xs text-secondary-600">
                    {provider.shortName} • {provider.locations.length} locations
                  </p>
                </div>

                <div className="text-right">
                  <div className="text-xs text-secondary-500 font-medium">Starting from</div>
                  <div className="text-sm font-bold text-secondary-900">
                    ${provider.pricing.vcpu}/vCPU/h
                  </div>
                </div>
              </div>

              {/* Compliance badges */}
              <div className="flex flex-wrap gap-1">
                {provider.gdprCompliant && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-800 border border-success-200">
                    GDPR
                  </span>
                )}
                {provider.nonUsCloudAct && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 border border-primary-200">
                    Non-US
                  </span>
                )}
                {provider.certifications.slice(0, 2).map((cert) => (
                  <span
                    key={cert}
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-800 border border-secondary-200"
                  >
                    {cert}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  )
}

export default ProviderSelector 